'use client';
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import MuxPlayer from '@mux/mux-player-react/lazy';
import { Radio } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cn } from '../../../lib/utils.js';
import { AUDIO_RECORDING_LABEL, AudioArtwork } from './audio-artwork';
import { findCaptionTrack, isShowingCaptionTrack } from './caption-tracks';
import { syncCaptionsMenuNote } from './captions-menu-note';
import { loadPlaylist } from './playlist-check';
var hasCaptionTrack = function (textTracks) {
    return Array.from(textTracks !== null && textTracks !== void 0 ? textTracks : []).some(function (_a) {
        var kind = _a.kind;
        return kind === 'subtitles' || kind === 'captions';
    });
};
/**
 * MuxVideoPlayer Component
 *
 * A standardized video player component using Mux Player for both
 * live streaming and video-on-demand playback.
 *
 * Features:
 * - Lazy loading for performance
 * - Built-in Mux Data analytics
 * - Transcript sync support via onTimeUpdate and ref.seek()
 * - Accessibility compliant with keyboard controls
 * - Support for live and on-demand content
 *
 * @example
 * ```tsx
 * // VOD playback
 * <MuxVideoPlayer
 *   playbackId="abc123"
 *   metadata={{ videoId: '123', videoTitle: 'Meeting Recording' }}
 *   onTimeUpdate={(time) => setCurrentTime(time)}
 * />
 *
 * // Live streaming
 * <MuxVideoPlayer
 *   playbackId="xyz789"
 *   streamType="live"
 *   isStreamActive={true}
 *   autoplay
 *   muted
 * />
 * ```
 */
export var MuxVideoPlayer = forwardRef(function (_a, ref) {
    var _b;
    var _c = _a.aspectRatio, aspectRatio = _c === void 0 ? '16/9' : _c, _d = _a.autoplay, autoplay = _d === void 0 ? false : _d, captionsMenuNote = _a.captionsMenuNote, className = _a.className, envKey = _a.envKey, _e = _a.forceAudioOnly, forceAudioOnly = _e === void 0 ? false : _e, _f = _a.isStreamActive, isStreamActive = _f === void 0 ? true : _f, metadata = _a.metadata, _g = _a.muted, muted = _g === void 0 ? false : _g, onAudioOnlyChange = _a.onAudioOnlyChange, onCaptionLanguageChange = _a.onCaptionLanguageChange, onDurationChange = _a.onDurationChange, onError = _a.onError, onPlayingChange = _a.onPlayingChange, onTimeUpdate = _a.onTimeUpdate, playbackId = _a.playbackId, poster = _a.poster, preferredCaptionLanguage = _a.preferredCaptionLanguage, src = _a.src, _h = _a.streamType, streamType = _h === void 0 ? 'on-demand' : _h, title = _a.title;
    var playerRef = useRef(null);
    var _j = useState(false), isAudioOnlyAsset = _j[0], setIsAudioOnlyAsset = _j[1];
    var _k = useState(false), hasCaptions = _k[0], setHasCaptions = _k[1];
    var _l = useState(null), loadedPlaylist = _l[0], setLoadedPlaylist = _l[1];
    var _m = useState(null), failedSrc = _m[0], setFailedSrc = _m[1];
    var hasLoadedRef = useRef(false);
    var preferredCaptionsSourceRef = useRef(null);
    var _o = useState(null), playedSource = _o[0], setPlayedSource = _o[1];
    var pendingSeekRef = useRef(null);
    // The element that last loaded media: a new one (e.g. after the src check remounts it) needs its own listener.
    var _p = useState(null), loadedPlayer = _p[0], setLoadedPlayer = _p[1];
    var useSrc = src !== undefined && failedSrc !== src;
    var shouldAutoDetectAudioOnly = streamType !== 'live' && !forceAudioOnly;
    var playlistInfo = useSrc && (loadedPlaylist === null || loadedPlaylist === void 0 ? void 0 : loadedPlaylist.src) === src ? loadedPlaylist.info : null;
    // The playlist's answer wins over the player's video dimensions, which only stand in when it has none.
    var playlistAudioOnly = (_b = playlistInfo === null || playlistInfo === void 0 ? void 0 : playlistInfo.isAudioOnly) !== null && _b !== void 0 ? _b : null;
    /**
     * Handle time update events from the player.
     * Extracts current time and passes to parent for transcript sync.
     */
    var handleTimeUpdate = useCallback(function () {
        var player = playerRef.current;
        if (player && onTimeUpdate) {
            onTimeUpdate(player.currentTime);
        }
    }, [onTimeUpdate]);
    /**
     * Handle duration change events.
     * Fires when the video metadata is loaded and duration is known.
     */
    var handleDurationChange = useCallback(function () {
        var player = playerRef.current;
        if (player && onDurationChange) {
            onDurationChange(player.duration);
        }
    }, [onDurationChange]);
    /**
     * Detect whether the loaded media is audio-only.
     * Mux audio-only assets report zero video dimensions.
     *
     * Detection runs on `loadeddata` (not `loadedmetadata`) because iOS Safari
     * uses Apple's native HLS engine, where `videoWidth` / `videoHeight` are
     * frequently still 0 in `loadedmetadata` for real video assets — that
     * caused archived videos to flash and then be replaced with the audio-only
     * card on iOS. By `loadeddata` the first media frame has been decoded and
     * the intrinsic dimensions are reliable.
     */
    var handleLoadedData = useCallback(function () {
        hasLoadedRef.current = true;
        setLoadedPlayer(playerRef.current);
        var player = playerRef.current;
        if (!player) {
            return;
        }
        if (!shouldAutoDetectAudioOnly || playlistAudioOnly !== null) {
            return;
        }
        var isAudioOnly = player.videoWidth === 0 && player.videoHeight === 0;
        setHasCaptions(hasCaptionTrack(player.textTracks));
        setIsAudioOnlyAsset(isAudioOnly);
        onAudioOnlyChange === null || onAudioOnlyChange === void 0 ? void 0 : onAudioOnlyChange(isAudioOnly);
    }, [onAudioOnlyChange, playlistAudioOnly, shouldAutoDetectAudioOnly]);
    /**
     * Safety net: the `resize` event fires whenever the underlying video's
     * intrinsic dimensions change. As soon as we observe non-zero dimensions
     * we know the asset is video, so make sure we are not stuck in audio-only
     * mode (e.g. if `loadeddata` hadn't fired yet on iOS Safari).
     */
    var handleResize = useCallback(function () {
        var player = playerRef.current;
        if (!player) {
            return;
        }
        if (!shouldAutoDetectAudioOnly || playlistAudioOnly !== null) {
            return;
        }
        if (player.videoWidth > 0 || player.videoHeight > 0) {
            setIsAudioOnlyAsset(false);
            onAudioOnlyChange === null || onAudioOnlyChange === void 0 ? void 0 : onAudioOnlyChange(false);
        }
    }, [onAudioOnlyChange, playlistAudioOnly, shouldAutoDetectAudioOnly]);
    /**
     * Handle play event - video started playing.
     */
    var handlePlay = useCallback(function () {
        // A preloaded (e.g. cached) source can fire `loadeddata` before the player's listeners attach.
        if (!hasLoadedRef.current)
            handleLoadedData();
        setPlayedSource(src !== null && src !== void 0 ? src : playbackId);
        onPlayingChange === null || onPlayingChange === void 0 ? void 0 : onPlayingChange(true);
    }, [handleLoadedData, onPlayingChange, playbackId, src]);
    /**
     * Handle pause event - video was paused.
     */
    var handlePause = useCallback(function () {
        onPlayingChange === null || onPlayingChange === void 0 ? void 0 : onPlayingChange(false);
    }, [onPlayingChange]);
    /**
     * Handle error events from the player. A `src` that fails before anything has loaded falls back to Mux.
     */
    var handleError = useCallback(function (event) {
        var _a;
        var errorEvent = event;
        var message = ((_a = errorEvent.detail) === null || _a === void 0 ? void 0 : _a.message) || 'Video playback error';
        if (useSrc && !hasLoadedRef.current) {
            console.warn('MuxVideoPlayer src failed, falling back to the Mux playback ID:', message);
            setFailedSrc(src);
            return;
        }
        console.error('MuxVideoPlayer error:', message);
        onError === null || onError === void 0 ? void 0 : onError(new Error(message));
    }, [onError, src, useSrc]);
    /**
     * Expose seek method via ref for external control.
     * Maintains compatibility with MediaPlayer interface.
     */
    useImperativeHandle(ref, function () { return ({
        seek: function (time) {
            var player = playerRef.current;
            if (!player) {
                // Held back while `src` is checked (e.g. a deep link seeking on load); applied once it mounts.
                pendingSeekRef.current = Math.max(0, time);
                onTimeUpdate === null || onTimeUpdate === void 0 ? void 0 : onTimeUpdate(pendingSeekRef.current);
                return;
            }
            var duration = player.duration || 0;
            var clampedTime = Math.max(0, Math.min(time, duration || time));
            player.currentTime = clampedTime;
            // Notify parent of time change for immediate UI update
            onTimeUpdate === null || onTimeUpdate === void 0 ? void 0 : onTimeUpdate(clampedTime);
        },
    }); }, [onTimeUpdate]);
    useEffect(function () {
        setIsAudioOnlyAsset(false);
        setHasCaptions(false);
        onAudioOnlyChange === null || onAudioOnlyChange === void 0 ? void 0 : onAudioOnlyChange(forceAudioOnly);
    }, [forceAudioOnly, onAudioOnlyChange, playbackId]);
    useEffect(function () {
        if (!shouldAutoDetectAudioOnly || playlistAudioOnly === null)
            return;
        onAudioOnlyChange === null || onAudioOnlyChange === void 0 ? void 0 : onAudioOnlyChange(playlistAudioOnly);
    }, [onAudioOnlyChange, playlistAudioOnly, shouldAutoDetectAudioOnly]);
    // The player's text tracks only exist once media has loaded; listen there for the viewer's caption choice.
    useEffect(function () {
        var textTracks = loadedPlayer === null || loadedPlayer === void 0 ? void 0 : loadedPlayer.textTracks;
        if (!onCaptionLanguageChange || !textTracks)
            return;
        var shown = null;
        var handleTrackChange = function () {
            var showing = Array.from(textTracks).find(function (_a) {
                var kind = _a.kind, mode = _a.mode;
                return (kind === 'subtitles' || kind === 'captions') && mode === 'showing';
            });
            var language = (showing === null || showing === void 0 ? void 0 : showing.language) || null;
            if (language === shown)
                return;
            shown = language;
            onCaptionLanguageChange(language);
        };
        // A remembered caption choice can already be showing before we listen.
        handleTrackChange();
        textTracks.addEventListener('change', handleTrackChange);
        return function () { return textTracks.removeEventListener('change', handleTrackChange); };
    }, [loadedPlayer, onCaptionLanguageChange]);
    useEffect(function () {
        if (!loadedPlayer || !captionsMenuNote)
            return;
        return syncCaptionsMenuNote(loadedPlayer, captionsMenuNote);
    }, [captionsMenuNote, loadedPlayer]);
    // Waits for playback so the AI notice doesn't pop up unprompted; tracks can arrive later, so keep looking.
    useEffect(function () {
        var textTracks = loadedPlayer === null || loadedPlayer === void 0 ? void 0 : loadedPlayer.textTracks;
        var source = src !== null && src !== void 0 ? src : playbackId;
        if (!textTracks ||
            !preferredCaptionLanguage ||
            playedSource !== source ||
            preferredCaptionsSourceRef.current === source) {
            return;
        }
        var handleTracksAdded = function () {
            var tracks = Array.from(textTracks);
            var preferredTrack = findCaptionTrack(tracks, preferredCaptionLanguage);
            if (tracks.some(isShowingCaptionTrack)) {
                preferredCaptionsSourceRef.current = source;
            }
            else if (preferredTrack) {
                preferredTrack.mode = 'showing';
                preferredCaptionsSourceRef.current = source;
            }
            else {
                return;
            }
            textTracks.removeEventListener('addtrack', handleTracksAdded);
        };
        handleTracksAdded();
        textTracks.addEventListener('addtrack', handleTracksAdded);
        return function () { return textTracks.removeEventListener('addtrack', handleTracksAdded); };
    }, [loadedPlayer, playbackId, playedSource, preferredCaptionLanguage, src]);
    useEffect(function () {
        var player = playerRef.current;
        if (pendingSeekRef.current === null || !player)
            return;
        player.currentTime = pendingSeekRef.current;
        pendingSeekRef.current = null;
    }, [loadedPlaylist, failedSrc]);
    // Check `src` before handing it to the player, so a 404 or an unreachable proxy plays Mux's playlist instead.
    useEffect(function () {
        hasLoadedRef.current = false;
        if (src === undefined)
            return;
        var cancelled = false;
        void loadPlaylist(src).then(function (info) {
            if (cancelled)
                return;
            if (info)
                setLoadedPlaylist({ src: src, info: info });
            else
                setFailedSrc(src);
        });
        return function () {
            cancelled = true;
        };
    }, [src]);
    var containerStyle = {
        aspectRatio: aspectRatio,
    };
    var audioControlsStyle = {
        aspectRatio: 'auto',
        '--controls-backdrop-color': 'rgb(0 0 0 / 0%)',
        '--title-display': 'none',
    };
    var isDetectedAudioOnly = shouldAutoDetectAudioOnly && (playlistAudioOnly !== null && playlistAudioOnly !== void 0 ? playlistAudioOnly : isAudioOnlyAsset);
    var hasCaptionTracks = playlistInfo !== null && playlistAudioOnly !== null ? playlistInfo.hasCaptions : hasCaptions;
    // Mux's audio layout has no captions menu or caption area, so captioned audio keeps the video layout over artwork.
    var captionedAudioMode = isDetectedAudioOnly && hasCaptionTracks;
    var audioOnlyMode = forceAudioOnly || (isDetectedAudioOnly && !hasCaptionTracks);
    var captionedAudioStyle = {
        '--media-background-color': 'transparent',
        '--pip-button': 'none',
        '--rendition-menu-button': 'none',
    };
    var resolvedStreamType = streamType === 'live' ? 'live' : 'on-demand';
    var muxMetadata = metadata
        ? {
            video_id: metadata.videoId,
            video_title: metadata.videoTitle,
            viewer_user_id: metadata.viewerUserId,
        }
        : undefined;
    // Without a playback ID the player can't derive Mux's poster and storyboard from the source, so name them.
    var srcPoster = poster !== null && poster !== void 0 ? poster : "https://image.mux.com/".concat(playbackId, "/thumbnail.webp");
    var muxImageProps = useSrc
        ? __assign({ src: src, envKey: envKey, 
            // `src` starts loading before the `metadata` prop lands, and Mux Data would fall back to the URL as video_id.
            'metadata-video-id': metadata === null || metadata === void 0 ? void 0 : metadata.videoId, 'metadata-video-title': metadata === null || metadata === void 0 ? void 0 : metadata.videoTitle, poster: srcPoster }, (resolvedStreamType === 'on-demand' && !captionedAudioMode
            ? { storyboardSrc: "https://image.mux.com/".concat(playbackId, "/storyboard.vtt") }
            : {})) : { playbackId: playbackId, poster: poster };
    var sharedMuxPlayerProps = {
        ref: playerRef,
        autoPlay: autoplay,
        crossOrigin: 'anonymous',
        metadata: muxMetadata,
        muted: muted,
        onDurationChange: handleDurationChange,
        onError: handleError,
        onLoadedData: handleLoadedData,
        onPause: handlePause,
        onPlay: handlePlay,
        onResize: handleResize,
        onTimeUpdate: handleTimeUpdate,
        placeholder: 'blur',
        streamType: resolvedStreamType,
    };
    // For live streams that aren't active, show placeholder
    if (streamType === 'live' && !isStreamActive) {
        return (<div style={containerStyle} className={cn('bg-background relative flex w-full items-center justify-center rounded-lg p-4', className)}>
          <div className="flex max-w-md flex-col items-center gap-3 text-center">
            <div className="bg-muted rounded-full p-4">
              <Radio className="text-muted-foreground h-12 w-12"/>
            </div>
            <div>
              <h3 className="text-foreground text-lg font-medium">Stream Not Active</h3>
              <p className="text-muted-foreground text-sm">
                Start the stream to begin live playback
              </p>
            </div>
          </div>
        </div>);
    }
    // Don't render player without a playback ID
    if (!playbackId) {
        return null;
    }
    // Hold the player until `src` is checked, rather than start Mux's playlist and swap it mid-load.
    if (useSrc && (loadedPlaylist === null || loadedPlaylist === void 0 ? void 0 : loadedPlaylist.src) !== src) {
        return (<div className={cn('relative w-full overflow-hidden bg-black', className)} style={containerStyle}>
          <img alt="" className="h-full w-full object-cover" src={srcPoster}/>
        </div>);
    }
    if (audioOnlyMode) {
        return (<div className={cn('bg-muted relative w-full overflow-hidden', className)} style={containerStyle}>
          <AudioArtwork poster={poster} title={title}/>
          {poster && (<div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-3 py-2">
              <p className="text-sm font-medium text-white">{AUDIO_RECORDING_LABEL}</p>
            </div>)}
          {/*
             * In audio mode Mux collapses the video to `height: 0`, so the player's
             * only UI is its control bar, which sizes to its natural (short) height.
             * Anchor it to the bottom rather than stretching it to fill the 16:9 box —
             * stretching with `h-full` pushes the control bar below the box, where the
             * container's `overflow-hidden` clips it (the original "no controls" bug).
             * `aspectRatio: 'auto'` (in audioControlsStyle) keeps the host from
             * re-imposing a 16:9 height on itself.
             */}
          <MuxPlayer {...sharedMuxPlayerProps} {...muxImageProps} audio className="absolute inset-x-0 bottom-0 z-10 w-full" 
        // Load eagerly instead of the default viewport-lazy facade: the lazy
        // placeholder is sized to a 16:9 box, which is incompatible with the
        // bottom-anchored, natural-height audio control bar above (the
        // placeholder would collapse to zero height and never upgrade).
        loading="page" style={audioControlsStyle} title={title !== null && title !== void 0 ? title : ''}/>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent"/>
        </div>);
    }
    return (<div className={cn('relative w-full overflow-hidden', captionedAudioMode && 'bg-black', className)} style={containerStyle}>
        {captionedAudioMode && <AudioArtwork poster={poster} title={title} variant="player"/>}
        <MuxPlayer {...sharedMuxPlayerProps} {...muxImageProps} {...(captionedAudioMode ? { poster: undefined } : {})} className="mux-player-cover absolute inset-0 h-full w-full" style={captionedAudioMode ? captionedAudioStyle : undefined} title={title}/>
      </div>);
});
MuxVideoPlayer.displayName = 'MuxVideoPlayer';
